import React from "react";
import {
  Brain,
  CheckCircle,
  RefreshCw,
  TrendingUp,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAds } from "../../hooks/AdsContext";
import { PlatformIcon } from "@/utils/platformIcons";

const AIAdvisorCard: React.FC = () => {
  const {
    suggesting,
    suggestedTargeting,
    handleSuggestTargeting,
    applySuggestedTargeting,
    platform,
  } = useAds();
  const { t } = useTranslation();

  return (
    <div className="bg-[#0f172a] rounded-2xl px-3.5 py-2.5 text-white shadow-md relative overflow-hidden font-sans border border-purple-900/30">
      {/* Background glow */}
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-600/10 blur-[60px] rounded-full" />

      <div className="relative z-10 flex flex-wrap lg:flex-nowrap items-center gap-2.5 lg:gap-3">
        {/* Header (icon + title, single line) */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Brain className="w-4 h-4 text-indigo-400 shrink-0" />
          <h2 className="text-xs font-bold text-white uppercase tracking-tight whitespace-nowrap">
            {t("Advisor IA")}
          </h2>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 lg:border-l lg:border-white/10 lg:pl-3">
          {suggestedTargeting ? (
            <div className="flex flex-wrap items-center gap-1.5 animate-in fade-in duration-300">
              <div className="flex items-center gap-1 bg-white/4 rounded-md px-2 py-0.5 border border-white/6">
                <TrendingUp className="w-3 h-3 text-indigo-300 shrink-0" />
                <span className="text-[10px] font-bold text-white whitespace-nowrap">
                  {suggestedTargeting.age_min || 18}-
                  {suggestedTargeting.age_max || 65} {t("anos")}
                </span>
              </div>
              <div className="flex items-center gap-1 bg-white/4 rounded-md px-2 py-0.5 border border-white/6">
                <PlatformIcon
                  platform="facebook"
                  className="w-3 h-3 text-indigo-300 shrink-0"
                />
                <span className="text-[10px] font-bold text-white whitespace-nowrap">
                  {suggestedTargeting.gender === "all"
                    ? t("Todos")
                    : suggestedTargeting.gender === "male"
                      ? t("Homens")
                      : t("Mulheres")}
                </span>
              </div>
              <div className="flex items-center gap-1 bg-white/4 rounded-md px-2 py-0.5 border border-white/6 min-w-0">
                <span className="text-[10px] font-bold text-white whitespace-nowrap">
                  {platform === "meta"
                    ? t("{count} interesses", {
                        count: suggestedTargeting.interests?.length || 0,
                      })
                    : t("{count} palavras-chave", {
                        count: suggestedTargeting.keywords?.length || 0,
                      })}
                </span>
                <span className="hidden sm:inline text-[9px] text-white/40 font-medium truncate">
                  {platform === "meta"
                    ? suggestedTargeting.interests
                        ?.slice(0, 2)
                        .map((i: any) => i.name)
                        .join(" · ")
                    : suggestedTargeting.keywords?.slice(0, 2).join(" · ")}
                </span>
                <ChevronRight className="w-3 h-3 text-indigo-400 shrink-0" />
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-indigo-200/80 font-medium truncate">
              {t(
                "Gere sugestões automáticas de idade, gênero e interesses com base na sua copy.",
              )}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0 ml-auto lg:ml-0">
          <div className="hidden lg:flex items-center gap-1.5 text-white/50 text-[10px] shrink-0">
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
            <span className="font-bold uppercase tracking-wider whitespace-nowrap">
              {t("Online")}
            </span>
          </div>
          {suggestedTargeting ? (
            <Button
              type="button"
              size="sm"
              className="min-h-0! py-1! px-2.5! bg-indigo-600 hover:bg-indigo-500 text-white border-none font-bold text-[10px] uppercase tracking-wider rounded-lg shadow-xs whitespace-nowrap"
              onClick={applySuggestedTargeting}
              icon={<CheckCircle className="w-3 h-3" />}
            >
              {t("Aplicar")}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={handleSuggestTargeting}
              disabled={suggesting}
              className="min-h-0! py-1! px-2.5! bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg font-bold text-[10px] uppercase tracking-wider transition-all whitespace-nowrap"
              icon={
                <RefreshCw
                  className={`w-3 h-3 ${suggesting ? "animate-spin" : ""}`}
                />
              }
            >
              {suggesting ? t("Analisando...") : t("Gerar com IA")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAdvisorCard;
